import { useCallback, useEffect, useState } from "react";

export function useDropdownPosition({
  open,
  anchorRef,
  maxPanelHeight = 350,
  minPanelHeight = 160,
  preferOpenUpWhenBelowLessThan = 220,
  gap = 8,
  margin = 12,
}) {
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: maxPanelHeight,
    placement: "bottom",
  });

  const update = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;

    const input = el.querySelector("input");
    if (!input) return;

    const r = input.getBoundingClientRect();
    const bottomTop = r.bottom + gap;

    const spaceBelow = window.innerHeight - bottomTop - margin;
    const spaceAbove = r.top - margin - gap;
    const openUp =
      spaceBelow < preferOpenUpWhenBelowLessThan && spaceAbove > spaceBelow;

    if (openUp) {
      const maxHeight = Math.max(
        minPanelHeight,
        Math.min(maxPanelHeight, spaceAbove),
      );
      setPos({
        top: r.top - gap,
        left: r.left,
        width: r.width,
        maxHeight,
        placement: "top",
      });
    } else {
      const maxHeight = Math.max(
        minPanelHeight,
        Math.min(maxPanelHeight, spaceBelow),
      );
      setPos({
        top: bottomTop,
        left: r.left,
        width: r.width,
        maxHeight,
        placement: "bottom",
      });
    }
  }, [
    anchorRef,
    gap,
    margin,
    maxPanelHeight,
    minPanelHeight,
    preferOpenUpWhenBelowLessThan,
  ]);

  useEffect(() => {
    if (!open) return;

    update();
    const onResize = () => update();
    const onScroll = () => update();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, update]);

  return { pos, update };
}
